"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronDown,
  Copy,
  Info,
  Share2,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useQueryClient } from "@tanstack/react-query";
import { SecuredFooter } from "../ui/secured-footer";
import {
  useActiveBusiness,
  useAcceptedCurrencies,
  useCancelDposSession,
  useCreateDposSession,
  useDposSessionQr,
  useDposSessionReceipt,
} from "../../hooks/query/dposQueries";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  type DposAcceptedCurrency,
  type DposCryptoFlow,
  type CreateDposSessionRequest,
  type DposSessionReceipt,
  type DposSessionDetails,
} from "../../services/dposService";
import { useToast } from "../../hooks/use-toast";
import {
  buildDposInfoRows,
  getDposStatusPresentation,
  isTerminalDposSessionStatus,
  resolveDposCryptoAmount,
  resolveDposCryptoAsset,
  resolveDposDeepLink,
  resolveDposNetwork,
  resolveDposPaymentLink,
  resolveDposPayId,
  resolveDposQrValue,
  resolveDposExternalWalletDisplayQr,
  resolveDposReference,
  resolveDposSessionId,
  resolveDposSessionStatus,
  resolveDposWalletAddress,
  type DposSessionMode,
} from "./dposPaymentSessionUtils";
import { DPOS_FIGMA_ASSETS } from "./dposFigmaAssets";
import DposLoadingCard from "./DposLoadingCard";
import { useDposSessionPersistence } from "./useDposSessionPersistence";
import {
  formatRandsDisplay,
  formatZarNumberForDisplay,
  ZAR_RANDS_ZERO,
} from "../../utils/zarDisplayFormat";

const eightyEightPayIcon = "/logo2.png";

const ZA_FLAG_URL = DPOS_FIGMA_ASSETS.southAfricaFlag;

interface DposAssetOption {
  assetId: string;
  symbol: string;
  assetName: string;
  chain: string | null;
  imageUrl: string | null;
  chainImageUrl: string | null;
}

interface DposSessionStepProps {
  amount: string;
  mode: DposSessionMode;
  onBack: () => void;
  onClose: () => void;
  onCreateNewPayment: () => void;
  onModeChange?: (mode: DposCryptoFlow) => void;
}

interface ModeConfig {
  title: string;
  description: string;
  successTitle: string;
  successDescription: (formattedAmount: string) => string;
  expiredTitle: string;
  expiredDescription: string;
  failedTitle: string;
  failedDescription: string;
  showQrLogo: boolean;
}

interface SessionPrimaryDetail {
  label: string;
  value: string;
}

interface ReceiptDetailRow {
  label: string;
  value: string;
  secondaryValue?: string | null;
  emphasize?: boolean;
  helperText?: string;
}

type CompletedSessionView = "success" | "receipt";

function SessionIconButton({
  onClick,
  disabled = false,
  position,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  position: "left" | "right";
  children: React.ReactNode;
}) {
  const positionClass = position === "left" ? "left-8" : "right-8";

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`absolute top-8 ${positionClass} border border-[#EBE9EF] rounded-full w-[48px] h-[48px] flex items-center justify-center hover:bg-[#F5F4F7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function buildAssetImageUrl(image: string | null): string | null {
  if (!image) {
    return null;
  }

  return `/images/coins/${image}`;
}

function normalizeChainLabel(chain: string | null | undefined): string | null {
  if (typeof chain !== "string" || !chain.trim()) {
    return null;
  }

  return chain.replace(/\s+network$/i, "").trim();
}

function findNativeChainAsset(
  asset: DposAcceptedCurrency,
  currencies: DposAcceptedCurrency[]
): DposAcceptedCurrency | undefined {
  const normalizedChain = normalizeChainLabel(asset.chain);
  if (!normalizedChain) {
    return undefined;
  }

  return currencies.find(
    (candidate) =>
      candidate.assetId !== asset.assetId &&
      candidate.assetName?.toLowerCase() === normalizedChain.toLowerCase()
  );
}

function formatExternalAssetSubtitle(
  chain: string | null | undefined,
  cryptoAmount: string | null,
  assetSymbol: string | null
): string {
  const chainLabel = normalizeChainLabel(chain);

  if (chainLabel && cryptoAmount && assetSymbol) {
    return `${chainLabel} • ${cryptoAmount} ${assetSymbol}`;
  }

  if (chainLabel) {
    return chainLabel;
  }

  if (cryptoAmount && assetSymbol) {
    return `${cryptoAmount} ${assetSymbol}`;
  }

  return assetSymbol || "";
}

function buildExternalWalletWarning(
  chain: string | null | undefined,
  assetSymbol: string | null
): string {
  const chainLabel = normalizeChainLabel(chain);

  if (chainLabel && assetSymbol) {
    return `You can only send ${chainLabel} (${assetSymbol}) assets to this wallet address. Other assets will be lost.`;
  }

  if (assetSymbol) {
    return `You can only send ${assetSymbol} assets to this wallet address. Other assets will be lost.`;
  }

  return "Only send the selected asset to this wallet address. Other assets will be lost.";
}

function getExternalWalletDescription(
  status: DposSessionDetails["status"]
): string {
  const resolvedStatus = resolveDposSessionStatus(status);

  if (resolvedStatus === "pending") {
    return "Transaction detected. Waiting for network confirmations.";
  }

  if (resolvedStatus === "processing") {
    return "Confirming payment on the blockchain.";
  }

  return "Open a compatible crypto wallet and scan this QR code.";
}

function formatCurrencyAmount(
  amount: string | number | null | undefined,
  options: {
    prefix?: string;
    fallback?: string;
  } = {}
): string {
  const parsedAmount =
    typeof amount === "number" ? amount : parseFloat(amount || "");

  if (!Number.isFinite(parsedAmount)) {
    return (
      options.fallback ?? (options.prefix === "R" ? ZAR_RANDS_ZERO : "0.00")
    );
  }

  if (options.prefix === "R") {
    return formatRandsDisplay(parsedAmount);
  }

  const formatted = formatZarNumberForDisplay(parsedAmount);
  return options.prefix ? `${options.prefix} ${formatted}` : formatted;
}

function formatReceiptTimestamp(timestamp: string | null | undefined): string {
  if (!timestamp) {
    return "Completed just now";
  }

  try {
    return new Date(timestamp).toLocaleString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return timestamp;
  }
}

function resolveReceiptPaidWith(
  mode: DposSessionMode,
  session: DposSessionDetails | null,
  receipt: DposSessionReceipt | null
): string {
  if (receipt?.paidWith) {
    return receipt.paidWith;
  }

  const cryptoAsset = resolveDposCryptoAsset(session);
  if (cryptoAsset) {
    return cryptoAsset;
  }

  if (mode === "88pay") {
    return "88Pay";
  }

  if (mode === "paylink") {
    return "PayLink";
  }

  if (mode === "internal_wallet") {
    return "80eight Wallet";
  }

  return "Crypto Wallet";
}

function resolveReceiptTotalCost(
  session: DposSessionDetails | null,
  receipt: DposSessionReceipt | null,
  amountLabel: string
): { value: string; secondaryValue: string | null } {
  const totalCost = receipt?.totalCost?.trim();
  const fiatEquivalent = receipt?.totalCostFiatEquivalent;

  if (totalCost) {
    return {
      value: totalCost,
      secondaryValue:
        fiatEquivalent && totalCost !== amountLabel
          ? `≈ ${formatCurrencyAmount(fiatEquivalent, { prefix: "R" })}`
          : null,
    };
  }

  const cryptoAmount = resolveDposCryptoAmount(session);
  const cryptoAsset = resolveDposCryptoAsset(session);
  if (cryptoAmount && cryptoAsset) {
    return {
      value: `${cryptoAmount} ${cryptoAsset}`,
      secondaryValue: `≈ ${amountLabel}`,
    };
  }

  return {
    value: amountLabel,
    secondaryValue: null,
  };
}

function PaymentSuccessIllustration() {
  return (
    <div className="relative h-[136px] w-[194px]" aria-hidden="true">
      <div className="absolute inset-[24.26%_14.95%_36.76%_57.73%] rounded-full bg-[#F1EDF7]" />
      <div className="absolute inset-[5.88%_82.47%_87.5%_12.89%]">
        <img
          src={DPOS_FIGMA_ASSETS.dposSuccessDot}
          alt=""
          className="block h-full w-full"
        />
      </div>
      <div className="absolute inset-[63.97%_70.1%_18.38%_17.53%]">
        <img
          src={DPOS_FIGMA_ASSETS.dposSuccessStar}
          alt=""
          className="block h-full w-full"
        />
      </div>
      <div className="absolute left-1/2 top-1/2 h-[103px] w-[103px] -translate-x-1/2 -translate-y-1/2 overflow-hidden">
        <div className="absolute inset-[3.57%]">
          <img
            src={DPOS_FIGMA_ASSETS.dposSuccessCard}
            alt=""
            className="block h-full w-full"
          />
        </div>
      </div>
    </div>
  );
}

function PaymentFailureIllustration() {
  return (
    <div className="relative h-[136px] w-[194px]" aria-hidden="true">
      <div className="absolute left-[27px] top-0 h-[136px] w-[136px] overflow-hidden">
        <img
          src={DPOS_FIGMA_ASSETS.dposErrorHand}
          alt=""
          className="block h-full w-full"
        />
      </div>
      <div className="absolute left-1/2 top-1/2 h-[104px] w-[104px] -translate-x-1/2 -translate-y-1/2 overflow-hidden">
        <img
          src={DPOS_FIGMA_ASSETS.dposErrorCircle}
          alt=""
          className="block h-full w-full"
        />
      </div>
      <div className="absolute left-1/2 top-1/2 h-[40px] w-[40px] -translate-x-1/2 -translate-y-1/2 overflow-hidden">
        <img
          src={DPOS_FIGMA_ASSETS.dposErrorDelete}
          alt=""
          className="block h-full w-full"
        />
      </div>
    </div>
  );
}

function DposFailureState({
  title,
  description,
  actionLabel,
  onAction,
  onClose,
}: {
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
  onClose?: () => void;
}) {
  return (
    <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
      {onClose ? (
        <SessionIconButton onClick={onClose} position="right">
          <X size={16} className="text-[#6D6584]" />
        </SessionIconButton>
      ) : null}

      <div className="flex flex-1 flex-col items-center justify-center w-full max-w-[794px] mx-auto px-8 pt-24 pb-12">
        <div className="flex flex-col gap-12 items-center text-center w-full py-12">
          <PaymentFailureIllustration />
          <div className="flex flex-col gap-6 items-center">
            <h2 className="text-[32px] font-semibold leading-[40px] text-[#33125F]">
              {title}
            </h2>
            <p className="max-w-[541px] text-[20px] font-normal leading-[32px] text-[#423E51]">
              {description}
            </p>
          </div>
          <button
            onClick={onAction}
            className="rounded-full h-[48px] min-w-[176px] px-8 text-white font-semibold text-[14px] hover:opacity-90 transition-opacity"
            style={{
              backgroundImage:
                "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
            }}
          >
            {actionLabel}
          </button>
        </div>
      </div>
      <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF] mt-auto" />
    </div>
  );
}

function ReceiptDetailTable({ rows }: { rows: ReceiptDetailRow[] }) {
  return (
    <div className="w-full rounded-[16px] border border-[#EBE9EF] bg-white py-4">
      {rows.map((row, index) => (
        <div key={row.label}>
          <div className="flex items-start justify-between gap-6 px-4 py-4">
            <div className="flex items-center gap-2">
              <p
                className={`text-[16px] leading-[20px] ${
                  row.emphasize
                    ? "font-semibold text-[#423E51]"
                    : "font-normal text-[#423E51]"
                }`}
              >
                {row.label}
              </p>
              {row.helperText ? (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#EB3366]"
                        aria-label={`${row.label} info`}
                      >
                        <Info size={14} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[220px]">
                      {row.helperText}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : null}
            </div>
            <div className="max-w-[60%] text-right">
              <p
                className={`break-words text-[20px] leading-[24px] text-[#423E51] ${
                  row.emphasize ? "font-semibold" : "font-semibold"
                }`}
              >
                {row.value}
              </p>
              {row.secondaryValue ? (
                <p className="mt-1 text-[14px] leading-[16px] text-[#6D6584]">
                  {row.secondaryValue}
                </p>
              ) : null}
            </div>
          </div>
          {index < rows.length - 1 ? (
            <div className="h-px w-full bg-[#EBE9EF]" />
          ) : null}
        </div>
      ))}
    </div>
  );
}

const MODE_CONFIG: Record<DposSessionMode, ModeConfig> = {
  "88pay": {
    title: "Scan with 80eight App to 88pay",
    description: "Open your 88Pay wallet and scan this QR code",
    successTitle: "Payment received!",
    successDescription: (formattedAmount) =>
      `R ${formattedAmount} has been received successfully.`,
    expiredTitle: "QR code expired",
    expiredDescription:
      "This payment request has expired. Please create a new one.",
    failedTitle: "Transaction failed",
    failedDescription: "The payment could not be processed. Please try again.",
    showQrLogo: true,
  },
  internal_wallet: {
    title: "Scan with 80eight App to pay",
    description:
      "The QR opens a pre-filled payment in the 80eight wallet so the customer can confirm securely in-app.",
    successTitle: "Payment received",
    successDescription: () => "Your payment has been received",
    expiredTitle: "QR code expired",
    expiredDescription:
      "The in-app payment request expired. Please create a new payment.",
    failedTitle: "Transaction failed",
    failedDescription: "The payment could not be processed. Please try again.",
    showQrLogo: true,
  },
  paylink: {
    title: "Scan or share Pay Link",
    description:
      "Let your customer scan the QR code or share the payment link to continue.",
    successTitle: "Pay Link paid!",
    successDescription: (formattedAmount) =>
      `The Pay Link payment for R ${formattedAmount} was received successfully.`,
    expiredTitle: "Pay Link expired",
    expiredDescription:
      "This Pay Link has expired. Please create a new payment.",
    failedTitle: "Transaction failed",
    failedDescription: "The payment could not be processed. Please try again.",
    showQrLogo: false,
  },
  external_wallet: {
    title: "Scan to pay with another crypto wallet",
    description: "Open a compatible crypto wallet and scan this QR code.",
    successTitle: "Payment received",
    successDescription: () => "Your payment has been received",
    expiredTitle: "QR code expired",
    expiredDescription:
      "This external wallet payment request expired. Please create a new one.",
    failedTitle: "Transaction failed",
    failedDescription:
      "Your crypto purchase was not successful. Please try again. If the problem persists, please contact support.",
    showQrLogo: true,
  },
};

function getCreateSessionPayload(
  amount: number,
  mode: DposSessionMode,
  cryptoAsset?: string | null
): CreateDposSessionRequest {
  if (mode === "88pay") {
    return {
      amount,
      currency: "ZAR",
      paymentMethod: "88pay",
    };
  }

  if (mode === "paylink") {
    return {
      amount,
      currency: "ZAR",
      paymentMethod: "paylink",
    };
  }

  return {
    amount,
    currency: "ZAR",
    paymentMethod: "crypto",
    cryptoFlow: mode,
    ...(cryptoAsset ? { cryptoAsset } : {}),
  };
}

export default function DposSessionStep({
  amount,
  mode,
  onBack,
  onClose,
  onCreateNewPayment,
  onModeChange,
}: DposSessionStepProps) {
  const config =
    mode === "88pay"
      ? MODE_CONFIG["88pay"]
      : mode === "paylink"
        ? MODE_CONFIG.paylink
        : mode === "internal_wallet"
          ? MODE_CONFIG.internal_wallet
          : MODE_CONFIG.external_wallet;
  const { businessId } = useActiveBusiness();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const assetMenuRef = useRef<HTMLDivElement | null>(null);

  const [sessionSeed, setSessionSeed] = useState<string | null>(null);
  const [createdSession, setCreatedSession] =
    useState<DposSessionDetails | null>(null);
  const [creationError, setCreationError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isClosingAction, setIsClosingAction] = useState(false);
  const [isAssetMenuOpen, setIsAssetMenuOpen] = useState(false);
  const [completedView, setCompletedView] =
    useState<CompletedSessionView>("success");
  const [selectedCryptoAsset, setSelectedCryptoAsset] = useState<string | null>(
    null
  );

  const attemptedRequestKey = useRef<string | null>(null);
  const prevStatus = useRef<string | undefined>(undefined);

  const {
    data: acceptedCurrencies = [],
    isLoading: isAcceptedCurrenciesLoading,
  } = useAcceptedCurrencies(mode === "external_wallet" ? businessId : null);
  const createMutation = useCreateDposSession(businessId);
  const cancelMutation = useCancelDposSession(businessId);
  const sessionQuery = useDposSessionQr(businessId, sessionSeed);
  const createSession = createMutation.mutateAsync;
  const resetCreateSession = createMutation.reset;

  const liveSession = sessionQuery.data || createdSession;
  const sessionId = resolveDposSessionId(liveSession);
  const receiptQuery = useDposSessionReceipt(
    businessId,
    completedView === "receipt" ? sessionId : null
  );
  const resolvedStatus = resolveDposSessionStatus(liveSession?.status);
  const deepLink = resolveDposDeepLink(liveSession);
  const paymentLink = resolveDposPaymentLink(liveSession);
  const payId = resolveDposPayId(liveSession);
  const reference = resolveDposReference(liveSession);
  const walletAddress = resolveDposWalletAddress(liveSession);
  const liveCryptoAsset = resolveDposCryptoAsset(liveSession);
  const liveCryptoAmount = resolveDposCryptoAmount(liveSession);
  const liveNetwork = resolveDposNetwork(liveSession);
  const infoRows = useMemo(
    () => buildDposInfoRows(liveSession, mode),
    [liveSession, mode]
  );

  const parsedAmount = useMemo(() => parseFloat(amount), [amount]);
  const formattedAmount = useMemo(
    () =>
      Number.isFinite(parsedAmount)
        ? formatZarNumberForDisplay(parsedAmount)
        : "0.00",
    [parsedAmount]
  );
  const acceptedCurrenciesList = (acceptedCurrencies ||
    []) as DposAcceptedCurrency[];
  const unavailableAcceptedAssetCount = useMemo(
    () =>
      acceptedCurrenciesList.filter(
        (currency) =>
          currency.isAccepted &&
          (!currency.businessWallet?.exists ||
            !currency.businessWallet?.address)
      ).length,
    [acceptedCurrenciesList]
  );
  const acceptedAssetOptions = useMemo(() => {
    const acceptedOnly = acceptedCurrenciesList.filter(
      (currency) =>
        currency.isAccepted &&
        currency.businessWallet?.exists &&
        Boolean(currency.businessWallet.address)
    );

    return acceptedOnly.map((currency): DposAssetOption => {
      const chainAsset = findNativeChainAsset(currency, acceptedCurrenciesList);

      return {
        assetId: currency.assetId,
        symbol: currency.symbol.toUpperCase(),
        assetName: currency.assetName,
        chain: currency.chain,
        imageUrl: buildAssetImageUrl(currency.image),
        chainImageUrl: buildAssetImageUrl(chainAsset?.image || null),
      };
    });
  }, [acceptedCurrenciesList]);
  const emptyExternalWalletDescription = useMemo(() => {
    if (unavailableAcceptedAssetCount > 0) {
      return "At least one accepted asset still needs a receive wallet. Create or finish wallet setup in POS receiving assets before starting an external wallet payment.";
    }

    return "Enable at least one accepted crypto asset before creating an external wallet payment.";
  }, [unavailableAcceptedAssetCount]);
  const fallbackAssetOption = useMemo<DposAssetOption | null>(() => {
    if (!liveCryptoAsset) {
      return null;
    }

    return {
      assetId: liveCryptoAsset,
      symbol: liveCryptoAsset,
      assetName: normalizeChainLabel(liveNetwork) || liveCryptoAsset,
      chain: liveNetwork,
      imageUrl: null,
      chainImageUrl: null,
    };
  }, [liveCryptoAsset, liveNetwork]);
  const statusPresentation = useMemo(
    () => getDposStatusPresentation(liveSession?.status),
    [liveSession?.status]
  );
  const isCompletedSession =
    resolvedStatus === "paid" || resolvedStatus === "confirmed";
  const resetStatusTracking = useCallback(() => {
    attemptedRequestKey.current = null;
    prevStatus.current = undefined;
  }, []);
  const { effectiveSelectedCryptoAsset, clearPersistedSession } =
    useDposSessionPersistence({
      businessId,
      mode,
      amount,
      acceptedAssetOptions,
      selectedCryptoAsset,
      completedView,
      isCompletedSession,
      sessionId,
      sessionSeed,
      resetCreateSession,
      setSessionSeed,
      setCreatedSession,
      setCreationError,
      setCopied,
      setIsAssetMenuOpen,
      setCompletedView,
      setSelectedCryptoAsset,
      resetStatusTracking,
    });
  const selectedAssetOption = useMemo(() => {
    if (effectiveSelectedCryptoAsset) {
      const matchedAsset = acceptedAssetOptions.find(
        (asset) => asset.symbol === effectiveSelectedCryptoAsset
      );

      if (matchedAsset) {
        return matchedAsset;
      }
    }

    return fallbackAssetOption;
  }, [acceptedAssetOptions, effectiveSelectedCryptoAsset, fallbackAssetOption]);

  const qrValue = useMemo(() => {
    if (mode === "external_wallet") {
      return resolveDposExternalWalletDisplayQr(liveSession, mode, {
        assetSymbol: selectedAssetOption?.symbol || liveCryptoAsset,
        chainLabel: selectedAssetOption?.chain || liveNetwork,
        amountCrypto: liveCryptoAmount,
      });
    }
    return resolveDposQrValue(liveSession, mode);
  }, [
    liveSession,
    mode,
    selectedAssetOption?.symbol,
    selectedAssetOption?.chain,
    liveCryptoAsset,
    liveNetwork,
    liveCryptoAmount,
  ]);

  const primaryDetail = useMemo<SessionPrimaryDetail | null>(() => {
    if (mode === "paylink") {
      const paymentDestination = paymentLink || qrValue;

      return paymentDestination
        ? {
            label: "Paylink",
            value: paymentDestination,
          }
        : null;
    }

    if (mode === "88pay") {
      const resolvedValue = payId || reference;

      return resolvedValue
        ? {
            label: payId ? "PayID" : "Reference",
            value: resolvedValue,
          }
        : null;
    }

    if (mode === "internal_wallet") {
      return walletAddress
        ? {
            label: "Wallet address",
            value: walletAddress,
          }
        : null;
    }

    return null;
  }, [mode, payId, paymentLink, qrValue, reference, walletAddress]);

  const supplementalRows = useMemo(
    () =>
      infoRows.filter(
        (row) =>
          !(
            (mode === "external_wallet" && row.label === "Wallet address") ||
            row.label === primaryDetail?.label
          )
      ),
    [infoRows, mode, primaryDetail]
  );

  const externalAssetSubtitle = useMemo(
    () =>
      formatExternalAssetSubtitle(
        selectedAssetOption?.chain || liveNetwork,
        liveCryptoAmount,
        selectedAssetOption?.symbol || liveCryptoAsset
      ),
    [liveCryptoAmount, liveCryptoAsset, liveNetwork, selectedAssetOption]
  );
  const externalWalletWarning = useMemo(
    () =>
      buildExternalWalletWarning(
        selectedAssetOption?.chain || liveNetwork,
        selectedAssetOption?.symbol || liveCryptoAsset
      ),
    [liveCryptoAsset, liveNetwork, selectedAssetOption]
  );
  const sessionDescription = useMemo(
    () =>
      mode === "external_wallet"
        ? getExternalWalletDescription(liveSession?.status)
        : config.description,
    [config.description, liveSession?.status, mode]
  );
  const shouldWaitForExternalAsset =
    mode === "external_wallet" &&
    (isAcceptedCurrenciesLoading ||
      (!effectiveSelectedCryptoAsset && acceptedAssetOptions.length > 0));
  const requestKey = useMemo(
    () =>
      `${businessId || "no-business"}:${mode}:${amount}:${
        effectiveSelectedCryptoAsset || "default"
      }`,
    [amount, businessId, effectiveSelectedCryptoAsset, mode]
  );

  useEffect(() => {
    if (mode !== "external_wallet") {
      setIsAssetMenuOpen(false);
      setSelectedCryptoAsset(null);
      return;
    }

    if (acceptedAssetOptions.length === 0) {
      return;
    }

    setSelectedCryptoAsset((currentSelection) => {
      if (
        currentSelection &&
        acceptedAssetOptions.some((asset) => asset.symbol === currentSelection)
      ) {
        return currentSelection;
      }

      if (
        liveCryptoAsset &&
        acceptedAssetOptions.some((asset) => asset.symbol === liveCryptoAsset)
      ) {
        return liveCryptoAsset;
      }

      return acceptedAssetOptions[0]?.symbol || null;
    });
  }, [acceptedAssetOptions, liveCryptoAsset, mode]);

  useEffect(() => {
    if (!isAssetMenuOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (
        assetMenuRef.current &&
        event.target instanceof Node &&
        !assetMenuRef.current.contains(event.target)
      ) {
        setIsAssetMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isAssetMenuOpen]);

  useEffect(() => {
    if (
      !businessId ||
      !Number.isFinite(parsedAmount) ||
      shouldWaitForExternalAsset ||
      attemptedRequestKey.current === requestKey ||
      !!liveSession ||
      !!sessionSeed
    ) {
      return;
    }

    attemptedRequestKey.current = requestKey;
    setCreationError(null);

    let ignore = false;

    void createSession(
      getCreateSessionPayload(parsedAmount, mode, effectiveSelectedCryptoAsset)
    )
      .then((data) => {
        if (ignore) {
          return;
        }
        setCreatedSession(data);
        setSessionSeed(resolveDposSessionId(data));
        toast({ description: "Payment session created successfully." });
        void queryClient.invalidateQueries({ queryKey: ["dposSessions"] });
      })
      .catch((error: unknown) => {
        if (ignore) {
          return;
        }
        const message =
          error instanceof Error
            ? error.message
            : "Failed to create payment session.";
        setCreationError(message);
        toast({
          description: message,
          variant: "destructive",
        });
      });

    return () => {
      ignore = true;
    };
  }, [
    businessId,
    createSession,
    liveSession,
    mode,
    parsedAmount,
    queryClient,
    requestKey,
    effectiveSelectedCryptoAsset,
    sessionSeed,
    shouldWaitForExternalAsset,
    toast,
  ]);

  useEffect(() => {
    const previousStatus = prevStatus.current;
    const currentStatus = liveSession?.status;

    if (!currentStatus || currentStatus === previousStatus) {
      return;
    }

    prevStatus.current = currentStatus;

    if (!previousStatus) {
      return;
    }

    if (resolvedStatus === "paid" || resolvedStatus === "confirmed") {
      toast({
        description: `Payment of R ${formattedAmount} received.`,
      });
      void queryClient.invalidateQueries({ queryKey: ["dposSessions"] });
      void queryClient.invalidateQueries({ queryKey: ["dposPerformance"] });
      return;
    }

    if (resolvedStatus === "expired") {
      toast({
        description: "Payment session expired.",
        variant: "destructive",
      });
      return;
    }

    if (resolvedStatus === "failed") {
      toast({
        description: "Payment failed.",
        variant: "destructive",
      });
    }
  }, [
    formattedAmount,
    liveSession?.status,
    queryClient,
    resolvedStatus,
    toast,
  ]);

  const copyTarget =
    primaryDetail?.value || walletAddress || paymentLink || deepLink || qrValue;
  const receiptData = receiptQuery.data;
  const amountPaidLabel = useMemo(
    () =>
      formatCurrencyAmount(
        receiptData?.amount || liveSession?.amount || amount,
        {
          prefix: "R",
          fallback: "R 0.00",
        }
      ),
    [amount, liveSession?.amount, receiptData?.amount]
  );
  const paidWithLabel = useMemo(
    () => resolveReceiptPaidWith(mode, liveSession, receiptData),
    [liveSession, mode, receiptData]
  );
  const receiptReference = useMemo(
    () => receiptData?.reference || resolveDposReference(liveSession) || "—",
    [liveSession, receiptData?.reference]
  );
  const receiptTimestamp = useMemo(
    () =>
      formatReceiptTimestamp(
        receiptData?.timestamp ||
          liveSession?.paidAt ||
          liveSession?.updatedAt ||
          liveSession?.createdAt ||
          null
      ),
    [
      liveSession?.createdAt,
      liveSession?.paidAt,
      liveSession?.updatedAt,
      receiptData?.timestamp,
    ]
  );
  const receiptTotalCost = useMemo(
    () => resolveReceiptTotalCost(liveSession, receiptData, amountPaidLabel),
    [amountPaidLabel, liveSession, receiptData]
  );
  const receiptRows = useMemo<ReceiptDetailRow[]>(
    () => [
      {
        label: "Transaction status",
        value: statusPresentation.label,
      },
      {
        label: "Paid with",
        value: paidWithLabel,
      },
      {
        label: "ZAR value",
        value: receiptData?.fiatEquivalent
          ? formatCurrencyAmount(receiptData.fiatEquivalent, { prefix: "R" })
          : amountPaidLabel,
      },
      {
        label: "Reference",
        value: receiptReference,
      },
      {
        label: "Network fee",
        value: receiptData?.networkFee?.trim() || "0.00",
        helperText:
          "Shown when the payment provider returns the final network fee.",
      },
      {
        label: "Total cost",
        value: receiptTotalCost.value,
        secondaryValue: receiptTotalCost.secondaryValue,
        emphasize: true,
      },
    ],
    [
      amountPaidLabel,
      paidWithLabel,
      receiptData?.fiatEquivalent,
      receiptData?.networkFee,
      receiptReference,
      receiptTotalCost.secondaryValue,
      receiptTotalCost.value,
      statusPresentation.label,
    ]
  );

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timeoutId = window.setTimeout(() => setCopied(false), 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [copied]);

  const handleCopy = async () => {
    if (!copyTarget) {
      return;
    }

    await navigator.clipboard.writeText(copyTarget);
    setCopied(true);
    toast({ description: "Payment request copied." });
  };

  const handleShare = async () => {
    if (!copyTarget) {
      return;
    }

    if (!navigator.share) {
      await handleCopy();
      return;
    }

    try {
      await navigator.share({
        title: "80eight payment request",
        text: copyTarget,
      });
    } catch (error) {
      if (!(error instanceof DOMException) || error.name !== "AbortError") {
        throw error;
      }
    }
  };

  const resetSessionForRetry = useCallback(() => {
    clearPersistedSession();
    resetStatusTracking();
    resetCreateSession();
    setCreatedSession(null);
    setSessionSeed(null);
    setCreationError(null);
    setCopied(false);
    setIsAssetMenuOpen(false);
    setCompletedView("success");
  }, [clearPersistedSession, resetCreateSession, resetStatusTracking]);

  const cancelCurrentSession = async () => {
    const targetSessionId = sessionId || sessionSeed;
    if (!targetSessionId) {
      return;
    }

    await cancelMutation.mutateAsync(targetSessionId);
    void queryClient.invalidateQueries({ queryKey: ["dposSessions"] });
  };

  const handleCreateNewPayment = () => {
    clearPersistedSession();
    onCreateNewPayment();
  };

  const handleRetryPayment = () => {
    resetSessionForRetry();
  };

  const handleBack = async () => {
    if (isClosingAction) {
      return;
    }

    setIsClosingAction(true);

    try {
      if (
        (sessionId || sessionSeed) &&
        !isTerminalDposSessionStatus(resolvedStatus)
      ) {
        await cancelCurrentSession();
      }
      clearPersistedSession();
      onBack();
    } catch {
      toast({
        description: "Could not cancel the payment session.",
        variant: "destructive",
      });
    } finally {
      setIsClosingAction(false);
    }
  };

  const handleClose = async () => {
    if (isClosingAction) {
      return;
    }

    setIsClosingAction(true);

    try {
      if (
        (sessionId || sessionSeed) &&
        !isTerminalDposSessionStatus(resolvedStatus)
      ) {
        await cancelCurrentSession();
        toast({ description: "Payment session cancelled." });
      }
      clearPersistedSession();
      onClose();
    } catch {
      toast({
        description: "Could not cancel the payment session.",
        variant: "destructive",
      });
    } finally {
      setIsClosingAction(false);
    }
  };

  if (shouldWaitForExternalAsset && !liveSession) {
    return <DposLoadingCard />;
  }

  if (
    mode === "external_wallet" &&
    !isAcceptedCurrenciesLoading &&
    acceptedAssetOptions.length === 0 &&
    !liveSession
  ) {
    return (
      <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
        <div className="flex flex-1 flex-col items-center justify-center w-full max-w-[794px] mx-auto px-8 py-16 text-center gap-6">
          <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F]">
            No receiving assets available
          </h2>
          <p className="text-[14px] font-normal leading-[20px] text-[#423E51] max-w-[420px]">
            {emptyExternalWalletDescription}
          </p>
          <div className="flex gap-4">
            <button
              onClick={onBack}
              className="border border-[#EBE9EF] rounded-full h-[48px] px-6 text-[#6D6584] font-semibold text-[14px] hover:bg-[#F5F4F7] transition-colors"
            >
              Go back
            </button>
            <button
              onClick={onClose}
              className="rounded-full h-[48px] px-6 text-white font-semibold text-[14px] hover:opacity-90 transition-opacity"
              style={{
                backgroundImage:
                  "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
              }}
            >
              Close
            </button>
          </div>
        </div>
        <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF] mt-auto" />
      </div>
    );
  }

  if (createMutation.isPending && !liveSession) {
    return <DposLoadingCard />;
  }

  if ((createMutation.isError || creationError) && !liveSession) {
    return (
      <DposFailureState
        title="Could not create payment"
        description={
          creationError ||
          (createMutation.error instanceof Error
            ? createMutation.error.message
            : "Something went wrong while preparing the payment request.")
        }
        actionLabel="Try Again"
        onAction={handleRetryPayment}
        onClose={() => {
          void handleClose();
        }}
      />
    );
  }

  if (isCompletedSession) {
    if (completedView === "receipt") {
      if (receiptQuery.isLoading) {
        return <DposLoadingCard />;
      }

      if (receiptQuery.isError) {
        return (
          <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
            <SessionIconButton
              onClick={() => {
                void handleClose();
              }}
              position="right"
            >
              <X size={16} className="text-[#6D6584]" />
            </SessionIconButton>

            <div className="flex flex-1 flex-col items-center justify-center w-full max-w-[794px] mx-auto px-8 pt-24 pb-12">
              <div className="flex flex-col gap-6 items-center text-center w-full py-12">
                <div className="w-20 h-20 rounded-full bg-[#FFF7ED] flex items-center justify-center">
                  <AlertCircle size={36} className="text-[#F97316]" />
                </div>
                <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F]">
                  Could not load receipt
                </h2>
                <p className="text-[14px] font-normal leading-[20px] text-[#423E51] max-w-[420px]">
                  {receiptQuery.error instanceof Error
                    ? receiptQuery.error.message
                    : "We could not load the payment summary right now."}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <button
                    onClick={() => setCompletedView("success")}
                    className="border border-[#EBE9EF] rounded-full h-[48px] px-6 text-[#6D6584] font-semibold text-[14px] hover:bg-[#F5F4F7] transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      void receiptQuery.refetch();
                    }}
                    className="border border-[#EBE9EF] rounded-full h-[48px] px-6 text-[#6D6584] font-semibold text-[14px] hover:bg-[#F5F4F7] transition-colors"
                  >
                    Try again
                  </button>
                  <button
                    onClick={() => {
                      void handleClose();
                    }}
                    className="rounded-full h-[48px] px-6 text-white font-semibold text-[14px] hover:opacity-90 transition-opacity"
                    style={{
                      backgroundImage:
                        "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
                    }}
                  >
                    Back to dashboard
                  </button>
                </div>
              </div>
            </div>
            <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF] mt-auto" />
          </div>
        );
      }

      return (
        <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
          <SessionIconButton
            onClick={() => {
              void handleClose();
            }}
            position="right"
          >
            <X size={16} className="text-[#6D6584]" />
          </SessionIconButton>

          <div className="flex flex-1 flex-col items-center justify-center w-full max-w-[954px] mx-auto px-6 pt-24 pb-10">
            <div className="flex flex-col gap-8 items-center justify-center w-full max-w-[796px]">
              <div className="w-full rounded-[20px] bg-[rgba(235,233,239,0.3)] px-6 py-8 text-center">
                <p className="text-[20px] font-normal leading-[24px] text-[#6D6584]">
                  Amount paid
                </p>
                <div className="mt-3 flex items-center justify-center gap-[11px]">
                  <img
                    src={ZA_FLAG_URL}
                    alt="ZAR"
                    className="w-10 h-10 rounded-full object-cover shrink-0"
                  />
                  <span className="text-[40px] font-bold leading-[32px] tracking-[-1.6px] text-[#423E51]">
                    {amountPaidLabel}
                  </span>
                </div>
              </div>

              <div className="w-full">
                <div className="mb-3 flex items-center justify-between gap-4">
                  <p className="text-[14px] font-semibold leading-[20px] text-[#6D6584]">
                    Transaction details
                  </p>
                  <p className="text-[13px] font-normal leading-[18px] text-[#9D96AF] text-right">
                    {receiptTimestamp}
                  </p>
                </div>
                <ReceiptDetailTable rows={receiptRows} />
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => setCompletedView("success")}
                  className="border border-[#EBE9EF] rounded-full w-[56px] h-[56px] flex items-center justify-center hover:bg-[#F5F4F7] transition-colors"
                  aria-label="Back"
                >
                  <ArrowLeft size={20} className="text-[#6D6584]" />
                </button>
                <button
                  onClick={() => {
                    void handleClose();
                  }}
                  className="rounded-full h-[52px] min-w-[240px] px-8 text-white font-semibold text-[16px] hover:opacity-90 transition-opacity"
                  style={{
                    backgroundImage:
                      "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
                  }}
                >
                  Back to dashboard
                </button>
              </div>
            </div>
          </div>
          <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF] mt-auto" />
        </div>
      );
    }

    return (
      <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
        <SessionIconButton
          onClick={() => {
            void handleClose();
          }}
          position="right"
        >
          <X size={16} className="text-[#6D6584]" />
        </SessionIconButton>

        <div className="flex flex-1 flex-col items-center justify-center w-full max-w-[794px] mx-auto px-8 pt-24 pb-12">
          <div className="flex flex-col gap-6 items-center text-center w-full py-12">
            <PaymentSuccessIllustration />
            <h2 className="text-[32px] font-semibold leading-[40px] text-[#33125F]">
              {config.successTitle}
            </h2>
            <div className="flex flex-col gap-2 items-center">
              <p className="text-[20px] font-normal leading-[32px] text-[#423E51] max-w-[380px]">
                {config.successDescription(formattedAmount)}
              </p>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={() => {
                  void handleBack();
                }}
                className="border border-[#EBE9EF] rounded-full w-[52px] h-[52px] flex items-center justify-center hover:bg-[#F5F4F7] transition-colors"
                aria-label="Back"
              >
                <ArrowLeft size={20} className="text-[#6D6584]" />
              </button>
              <button
                onClick={() => setCompletedView("receipt")}
                className="border border-[#EBE9EF] rounded-full h-[48px] px-6 text-[#6D6584] font-semibold text-[14px] hover:bg-[#F5F4F7] transition-colors"
              >
                View receipt
              </button>
              <button
                onClick={handleCreateNewPayment}
                className="rounded-full h-[48px] px-6 text-white font-semibold text-[14px] hover:opacity-90 transition-opacity"
                style={{
                  backgroundImage:
                    "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
                }}
              >
                Create New Payment
              </button>
            </div>
          </div>
        </div>
        <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF] mt-auto" />
      </div>
    );
  }

  if (resolvedStatus === "failed") {
    return (
      <DposFailureState
        title={config.failedTitle}
        description={liveSession?.errorMessage || config.failedDescription}
        actionLabel="Try Again"
        onAction={handleRetryPayment}
        onClose={() => {
          void handleClose();
        }}
      />
    );
  }

  if (resolvedStatus === "expired") {
    const title = config.expiredTitle;
    const description = config.expiredDescription;

    return (
      <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
        <SessionIconButton
          onClick={() => {
            void handleBack();
          }}
          position="left"
        >
          <ArrowLeft size={16} className="text-[#6D6584]" />
        </SessionIconButton>
        <SessionIconButton
          onClick={() => {
            void handleClose();
          }}
          position="right"
        >
          <X size={16} className="text-[#6D6584]" />
        </SessionIconButton>

        <div className="flex flex-1 flex-col items-center justify-center w-full max-w-[794px] mx-auto px-8 pt-24 pb-12">
          <div className="flex flex-col gap-6 items-center text-center w-full py-12">
            <div
              className={`w-20 h-20 rounded-full flex items-center justify-center ${
                resolvedStatus === "expired" ? "bg-[#FFF7ED]" : "bg-[#FFF1F2]"
              }`}
            >
              <X
                size={40}
                className={
                  resolvedStatus === "expired"
                    ? "text-[#F97316]"
                    : "text-[#EB3366]"
                }
              />
            </div>
            <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F]">
              {title}
            </h2>
            <p className="text-[14px] font-normal leading-[20px] text-[#423E51] max-w-[420px]">
              {description}
            </p>
            <div className="flex gap-4 mt-4">
              <button
                onClick={() => {
                  void handleBack();
                }}
                className="border border-[#EBE9EF] rounded-full h-[48px] px-6 text-[#6D6584] font-semibold text-[14px] hover:bg-[#F5F4F7] transition-colors"
              >
                Go back
              </button>
              <button
                onClick={handleCreateNewPayment}
                className="rounded-full h-[48px] px-6 text-white font-semibold text-[14px] hover:opacity-90 transition-opacity"
                style={{
                  backgroundImage:
                    "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)",
                }}
              >
                New payment
              </button>
            </div>
          </div>
        </div>
        <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF] mt-auto" />
      </div>
    );
  }

  if (mode === "paylink") {
    return (
      <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
        <SessionIconButton
          onClick={handleClose}
          disabled={isClosingAction}
          position="right"
        >
          <img
            src={DPOS_FIGMA_ASSETS.closeDelete}
            alt=""
            aria-hidden="true"
            className="h-4 w-4 object-contain"
          />
        </SessionIconButton>

        <div className="flex flex-1 flex-col items-center justify-center w-full max-w-[954px] mx-auto px-[80px] pt-24 pb-10">
          <div className="flex flex-col gap-10 items-center justify-center w-full">
            <div className="flex flex-col gap-6 items-center text-center w-full">
              <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F] max-w-[414px]">
                Scan QR code or share the link to receive payment
              </h2>
            </div>

            <div className="w-full rounded-[16px] bg-[rgba(235,233,239,0.3)] h-[182px] px-6 flex items-center justify-between gap-6">
              <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <p className="text-[20px] font-bold leading-[24px] text-[#6D6584]">
                  Amount
                </p>
                <div className="flex items-center gap-[11px]">
                  <img
                    src={ZA_FLAG_URL}
                    alt="ZAR"
                    className="w-10 h-10 rounded-full object-cover shrink-0"
                  />
                  <span className="text-[40px] font-bold leading-[32px] tracking-[-1.6px] text-[#423E51]">
                    R {formattedAmount}
                  </span>
                </div>
              </div>

              <div className="bg-white border border-[#EBE9EF] rounded-[13px] p-[13px] w-[165px] h-[165px] flex items-center justify-center shrink-0">
                {qrValue ? (
                  <QRCodeSVG
                    value={qrValue}
                    size={139}
                    level="H"
                    imageSettings={{
                      src: eightyEightPayIcon,
                      width: 43,
                      height: 43,
                      excavate: true,
                    }}
                  />
                ) : (
                  <p className="text-[12px] text-[#EB3366] text-center max-w-[120px]">
                    QR details are not available yet.
                  </p>
                )}
              </div>
            </div>

            {primaryDetail ? (
              <div className="w-full rounded-[16px] border border-[#EBE9EF] bg-white px-6 py-4 flex items-center justify-between gap-6">
                <div className="flex flex-col gap-3 min-w-0 flex-1">
                  <p className="text-[16px] font-bold leading-[24px] text-[#6D6584]">
                    Paylink
                  </p>
                  <p className="text-[16px] font-semibold leading-[24px] text-[#6D6584] break-all">
                    {primaryDetail.value}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={handleCopy}
                    disabled={!copyTarget || isClosingAction}
                    className={`border border-[#EBE9EF] rounded-full w-[46px] h-[46px] flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      copied
                        ? "bg-[#EB3366] border-[#EB3366]"
                        : "hover:bg-[#F5F4F7]"
                    }`}
                    title={copied ? "Copied!" : "Copy"}
                  >
                    {copied ? (
                      <Check size={14} className="text-white" />
                    ) : (
                      <img
                        src={DPOS_FIGMA_ASSETS.paylinkCopy}
                        alt=""
                        aria-hidden="true"
                        className="h-3 w-3 object-contain"
                      />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      void handleShare().catch(() => {
                        toast({
                          description: "Could not share the payment request.",
                          variant: "destructive",
                        });
                      });
                    }}
                    disabled={!copyTarget || isClosingAction}
                    className="border border-[#EBE9EF] rounded-full w-[48px] h-[48px] flex items-center justify-center hover:bg-[#F5F4F7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Share"
                  >
                    <img
                      src={DPOS_FIGMA_ASSETS.paylinkShare}
                      alt=""
                      aria-hidden="true"
                      className="h-4 w-4 object-contain"
                    />
                  </button>
                </div>
              </div>
            ) : null}

            <div className="flex items-center justify-center gap-4 w-full">
              <button
                onClick={handleBack}
                disabled={isClosingAction}
                className="border border-[#EBE9EF] rounded-full w-[64px] h-[64px] flex items-center justify-center hover:bg-[#F5F4F7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <img
                  src={DPOS_FIGMA_ASSETS.backNav}
                  alt=""
                  aria-hidden="true"
                  className="h-6 w-6 object-contain"
                />
              </button>
              <div className="rounded-full bg-[rgba(235,233,239,0.3)] h-[64px] px-6 flex items-center justify-center">
                <span className="text-[16px] font-bold leading-[20px] text-[#9D96AF] whitespace-nowrap">
                  {statusPresentation.label}
                </span>
              </div>
            </div>
          </div>
        </div>

        <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF] mt-auto" />
      </div>
    );
  }

  if (mode === "88pay") {
    return (
      <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
        <SessionIconButton
          onClick={handleClose}
          disabled={isClosingAction}
          position="right"
        >
          <img
            src={DPOS_FIGMA_ASSETS.closeDelete}
            alt=""
            aria-hidden="true"
            className="h-4 w-4 object-contain"
          />
        </SessionIconButton>

        <div className="flex flex-1 flex-col items-center justify-center w-full max-w-[954px] mx-auto px-[80px] pt-24 pb-10">
          <div className="flex flex-col gap-10 items-center justify-center w-full">
            <div className="flex flex-col gap-6 items-center text-center w-full">
              <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F] max-w-[414px]">
                {config.title}
              </h2>
              <p className="text-[14px] font-normal leading-[20px] text-[#423E51]">
                {config.description}
              </p>
            </div>

            <div className="w-full rounded-[16px] bg-[rgba(235,233,239,0.3)] h-[182px] px-6 flex items-center justify-between gap-6">
              <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <p className="text-[20px] font-normal leading-[24px] text-[#6D6584]">
                  Amount
                </p>
                <div className="flex items-center gap-[11px]">
                  <img
                    src={ZA_FLAG_URL}
                    alt="ZAR"
                    className="w-10 h-10 rounded-full object-cover shrink-0"
                  />
                  <span className="text-[40px] font-bold leading-[32px] tracking-[-1.6px] text-[#423E51]">
                    R {formattedAmount}
                  </span>
                </div>
              </div>

              <div className="bg-white border border-[#EBE9EF] rounded-[13px] p-[13px] w-[165px] h-[165px] flex items-center justify-center shrink-0">
                {qrValue ? (
                  <QRCodeSVG
                    value={qrValue}
                    size={139}
                    level="H"
                    imageSettings={{
                      src: eightyEightPayIcon,
                      width: 43,
                      height: 43,
                      excavate: true,
                    }}
                  />
                ) : (
                  <p className="text-[12px] text-[#EB3366] text-center max-w-[120px]">
                    QR details are not available yet.
                  </p>
                )}
              </div>
            </div>

            {primaryDetail ? (
              <div className="w-full rounded-[16px] border border-[#EBE9EF] bg-white px-6 py-4 flex items-center justify-between gap-6">
                <div className="flex flex-col gap-3 min-w-0 flex-1">
                  <p className="text-[16px] font-normal leading-[24px] text-[#6D6584]">
                    PayID:
                  </p>
                  <p className="text-[16px] font-semibold leading-[24px] text-[#6D6584] break-all">
                    {primaryDetail.value}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={handleCopy}
                    disabled={!copyTarget || isClosingAction}
                    className={`border border-[#EBE9EF] rounded-full w-[49px] h-[49px] flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      copied
                        ? "bg-[#EB3366] border-[#EB3366]"
                        : "hover:bg-[#F5F4F7]"
                    }`}
                    title={copied ? "Copied!" : "Copy"}
                  >
                    {copied ? (
                      <Check size={14} className="text-white" />
                    ) : (
                      <img
                        src={DPOS_FIGMA_ASSETS.paylinkCopy}
                        alt=""
                        aria-hidden="true"
                        className="h-3 w-3 object-contain"
                      />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      void handleShare().catch(() => {
                        toast({
                          description: "Could not share the payment request.",
                          variant: "destructive",
                        });
                      });
                    }}
                    disabled={!copyTarget || isClosingAction}
                    className="border border-[#EBE9EF] rounded-full w-[48px] h-[48px] flex items-center justify-center hover:bg-[#F5F4F7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Share"
                  >
                    <img
                      src={DPOS_FIGMA_ASSETS.paylinkShare}
                      alt=""
                      aria-hidden="true"
                      className="h-4 w-4 object-contain"
                    />
                  </button>
                </div>
              </div>
            ) : null}

            <div className="flex items-center justify-center gap-4 w-full">
              <button
                onClick={handleBack}
                disabled={isClosingAction}
                className="border border-[#EBE9EF] rounded-full w-[64px] h-[64px] flex items-center justify-center hover:bg-[#F5F4F7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <img
                  src={DPOS_FIGMA_ASSETS.backNav}
                  alt=""
                  aria-hidden="true"
                  className="h-6 w-6 object-contain"
                />
              </button>
              <div className="rounded-full bg-[rgba(235,233,239,0.3)] h-[64px] px-6 flex items-center justify-center">
                <span className="text-[16px] font-normal leading-[20px] text-[#6D6584] whitespace-nowrap">
                  {statusPresentation.label}
                </span>
              </div>
            </div>
          </div>
        </div>

        <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF] mt-auto" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
      <SessionIconButton
        onClick={handleBack}
        disabled={isClosingAction}
        position="left"
      >
        <ArrowLeft size={16} className="text-[#6D6584]" />
      </SessionIconButton>
      <SessionIconButton
        onClick={handleClose}
        disabled={isClosingAction}
        position="right"
      >
        <X size={16} className="text-[#6D6584]" />
      </SessionIconButton>

      <div className="flex flex-1 flex-col items-center w-full max-w-[954px] mx-auto px-8 pt-24">
        <div className="flex flex-col gap-6 items-center text-center w-full pb-6">
          <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F] max-w-[514px]">
            {config.title}
          </h2>
          <p className="text-[14px] font-normal leading-[20px] text-[#423E51]">
            {sessionDescription}
          </p>
        </div>

        {(mode === "internal_wallet" || mode === "external_wallet") && (
          <div className="w-full px-[80px] pb-6">
            <div className="w-full rounded-full bg-[rgba(235,233,239,0.3)] p-2 flex items-center gap-2">
              <button
                type="button"
                disabled={mode === "88pay" || !onModeChange}
                onClick={() => onModeChange?.("internal_wallet")}
                className={`flex-1 h-[48px] rounded-full text-[14px] font-semibold leading-[20px] transition-colors ${
                  mode !== "external_wallet"
                    ? "bg-[#060606] text-white"
                    : "text-[#6D6584]"
                }`}
              >
                Pay with 80eight App
              </button>
              <button
                type="button"
                disabled={mode === "88pay" || !onModeChange}
                onClick={() => onModeChange?.("external_wallet")}
                className={`flex-1 h-[48px] rounded-full text-[14px] font-semibold leading-[20px] transition-colors ${
                  mode === "external_wallet"
                    ? "bg-[#060606] text-white"
                    : "text-[#6D6584]"
                }`}
              >
                Pay with another wallet
              </button>
            </div>
          </div>
        )}

        {mode === "external_wallet" ? (
          <div className="w-full px-[80px] pb-10">
            <div className="flex flex-col gap-10 items-center justify-center w-full">
              <div className="flex flex-col lg:flex-row items-start gap-6 w-full">
                <div className="flex-1 self-stretch">
                  <div className="flex flex-col gap-6 h-full">
                    {unavailableAcceptedAssetCount > 0 ? (
                      <div className="flex items-start gap-3 rounded-[16px] border border-[#F4D2DC] bg-[#FFF6F9] px-4 py-3 text-[#7A3450]">
                        <AlertCircle size={18} className="mt-0.5 shrink-0" />
                        <p className="text-[13px] leading-[20px]">
                          Some accepted assets are hidden here until their
                          receive wallet is ready.
                        </p>
                      </div>
                    ) : null}
                    <div className="w-full" ref={assetMenuRef}>
                      <p className="text-[14px] font-normal leading-[20px] text-[#6D6584] mb-3">
                        Select asset to receive
                      </p>
                      <div className="relative">
                        <button
                          type="button"
                          disabled={
                            acceptedAssetOptions.length <= 1 || isClosingAction
                          }
                          onClick={() =>
                            setIsAssetMenuOpen((currentOpen) => !currentOpen)
                          }
                          className="w-full min-h-[88px] rounded-[16px] border border-[#CECBD7] bg-white px-6 py-5 flex items-center justify-between text-left disabled:cursor-default"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {selectedAssetOption?.imageUrl ? (
                              <div className="relative shrink-0 w-10 h-10">
                                <img
                                  src={selectedAssetOption.imageUrl}
                                  alt={selectedAssetOption.symbol}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                                {selectedAssetOption.chainImageUrl ? (
                                  <img
                                    src={selectedAssetOption.chainImageUrl}
                                    alt={selectedAssetOption.chain || "chain"}
                                    className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border border-white object-cover"
                                  />
                                ) : null}
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-[#EBE9EF] flex items-center justify-center shrink-0 text-[14px] font-semibold text-[#6D6584]">
                                {(selectedAssetOption?.symbol || "?").slice(
                                  0,
                                  2
                                )}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <p className="text-[16px] font-semibold leading-[24px] text-[#423E51] whitespace-nowrap">
                                  {selectedAssetOption?.symbol || "Asset"}
                                </p>
                                {selectedAssetOption?.chain ? (
                                  <span className="rounded-full bg-[rgba(235,233,239,0.3)] px-2 py-[6px] text-[10px] font-bold leading-[14px] tracking-[0.5px] uppercase text-[#6D6584] whitespace-nowrap">
                                    {normalizeChainLabel(
                                      selectedAssetOption.chain
                                    )}
                                  </span>
                                ) : null}
                              </div>
                              <p className="text-[14px] font-normal leading-[20px] text-[#6D6584] truncate">
                                {externalAssetSubtitle ||
                                  selectedAssetOption?.assetName ||
                                  " "}
                              </p>
                            </div>
                          </div>
                          <ChevronDown
                            size={16}
                            className="text-[#6D6584] shrink-0"
                          />
                        </button>

                        {isAssetMenuOpen && acceptedAssetOptions.length > 1 ? (
                          <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-10 rounded-[16px] border border-[#EBE9EF] bg-white shadow-[0_20px_40px_rgba(51,18,95,0.08)] overflow-hidden">
                            {acceptedAssetOptions.map((asset) => (
                              <button
                                key={asset.assetId}
                                type="button"
                                onClick={() => {
                                  setSelectedCryptoAsset(asset.symbol);
                                  setIsAssetMenuOpen(false);
                                }}
                                className={`w-full px-6 py-4 flex items-center justify-between text-left transition-colors ${
                                  selectedCryptoAsset === asset.symbol
                                    ? "bg-[#FBFAFD]"
                                    : "hover:bg-[#FBFAFD]"
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  {asset.imageUrl ? (
                                    <div className="relative shrink-0 w-10 h-10">
                                      <img
                                        src={asset.imageUrl}
                                        alt={asset.symbol}
                                        className="w-10 h-10 rounded-full object-cover"
                                      />
                                      {asset.chainImageUrl ? (
                                        <img
                                          src={asset.chainImageUrl}
                                          alt={asset.chain || "chain"}
                                          className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border border-white object-cover"
                                        />
                                      ) : null}
                                    </div>
                                  ) : (
                                    <div className="w-10 h-10 rounded-full bg-[#EBE9EF] flex items-center justify-center shrink-0 text-[14px] font-semibold text-[#6D6584]">
                                      {asset.symbol.slice(0, 2)}
                                    </div>
                                  )}
                                  <div className="min-w-0">
                                    <p className="text-[16px] font-semibold leading-[24px] text-[#423E51]">
                                      {asset.symbol}
                                    </p>
                                    <p className="text-[14px] font-normal leading-[20px] text-[#6D6584] truncate">
                                      {normalizeChainLabel(asset.chain) ||
                                        asset.assetName}
                                    </p>
                                  </div>
                                </div>
                                {selectedCryptoAsset === asset.symbol ? (
                                  <Check size={16} className="text-[#57249C]" />
                                ) : null}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="w-full">
                      <p className="text-[14px] font-normal leading-[20px] text-[#6D6584] mb-3">
                        Wallet address
                      </p>
                      <div className="w-full h-[88px] rounded-[16px] border border-[#CECBD7] bg-white flex items-center justify-between px-6">
                        <p className="text-[16px] font-normal leading-[24px] text-[#9D96AF] truncate pr-4">
                          {walletAddress || " "}
                        </p>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={handleCopy}
                            disabled={!copyTarget || isClosingAction}
                            className={`border border-[#EBE9EF] rounded-full w-10 h-10 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                              copied
                                ? "bg-[#EB3366] border-[#EB3366]"
                                : "hover:bg-[#F5F4F7]"
                            }`}
                            title={copied ? "Copied!" : "Copy"}
                          >
                            {copied ? (
                              <Check size={14} className="text-white" />
                            ) : (
                              <Copy size={12} className="text-[#6D6584]" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              void handleShare().catch(() => {
                                toast({
                                  description:
                                    "Could not share the payment request.",
                                  variant: "destructive",
                                });
                              });
                            }}
                            disabled={!copyTarget || isClosingAction}
                            className="border border-[#EBE9EF] rounded-full w-10 h-10 flex items-center justify-center hover:bg-[#F5F4F7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Share"
                          >
                            <Share2 size={16} className="text-[#6D6584]" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-[#EBE9EF] rounded-[13px] p-[13px] w-[240px] h-[245px] flex items-center justify-center shrink-0 self-center lg:self-start">
                  {qrValue ? (
                    <div className="relative">
                      <QRCodeSVG
                        value={qrValue}
                        size={214}
                        level="H"
                        imageSettings={
                          config.showQrLogo
                            ? {
                                src: eightyEightPayIcon,
                                width: 68,
                                height: 68,
                                excavate: true,
                              }
                            : undefined
                        }
                      />
                    </div>
                  ) : (
                    <p className="text-[12px] text-[#EB3366] text-center max-w-[150px]">
                      QR details are not available yet.
                    </p>
                  )}
                </div>
              </div>

              <div className="w-full rounded-[16px] bg-[#FFF2EB] min-h-[48px] px-6 py-3 flex items-center">
                <div className="flex items-center gap-2 text-[#974D21]">
                  <AlertCircle size={16} className="shrink-0" />
                  <p className="text-[14px] font-semibold leading-[20px]">
                    {externalWalletWarning}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full px-[80px] pb-10">
            <div className="flex items-center gap-6 w-full">
              <div className="flex-1 self-stretch">
                <div className="flex flex-col gap-6 h-full">
                  <div className="bg-[rgba(235,233,239,0.3)] rounded-[16px] h-[125px] flex flex-col items-center justify-center">
                    <p className="text-[20px] font-normal leading-[24px] text-[#6D6584] mb-3">
                      Amount to Pay
                    </p>
                    <div className="flex items-center gap-[11px]">
                      <img
                        src={ZA_FLAG_URL}
                        alt="ZAR"
                        className="w-10 h-10 rounded-full object-cover shrink-0"
                      />
                      <span className="text-[40px] font-bold leading-[32px] tracking-[-1.6px] text-[#423E51]">
                        R {formattedAmount}
                      </span>
                    </div>
                  </div>

                  {primaryDetail ? (
                    <div className="w-full">
                      <p className="text-[14px] font-normal leading-[20px] text-[#6D6584] mb-3">
                        {primaryDetail.label}
                      </p>
                      <div className="w-full min-h-[88px] rounded-[16px] border border-[#CECBD7] bg-white flex items-center justify-between gap-4 px-6 py-5">
                        <p className="text-[16px] font-normal leading-[24px] text-[#423E51] break-all pr-4">
                          {primaryDetail.value}
                        </p>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={handleCopy}
                            disabled={!copyTarget || isClosingAction}
                            className={`border border-[#EBE9EF] rounded-full w-10 h-10 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                              copied
                                ? "bg-[#EB3366] border-[#EB3366]"
                                : "hover:bg-[#F5F4F7]"
                            }`}
                            title={copied ? "Copied!" : "Copy"}
                          >
                            {copied ? (
                              <Check size={14} className="text-white" />
                            ) : (
                              <Copy size={12} className="text-[#6D6584]" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              void handleShare().catch(() => {
                                toast({
                                  description:
                                    "Could not share the payment request.",
                                  variant: "destructive",
                                });
                              });
                            }}
                            disabled={!copyTarget || isClosingAction}
                            className="border border-[#EBE9EF] rounded-full w-10 h-10 flex items-center justify-center hover:bg-[#F5F4F7] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Share"
                          >
                            <Share2 size={16} className="text-[#6D6584]" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {supplementalRows.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {supplementalRows.map((row) => (
                        <div
                          key={`${row.label}-${row.value}`}
                          className="rounded-[16px] border border-[#EBE9EF] bg-[#FBFAFD] px-5 py-4 text-left"
                        >
                          <p className="text-[12px] font-medium leading-[16px] text-[#9D96AF] mb-1">
                            {row.label}
                          </p>
                          <p className="text-[14px] font-semibold leading-[20px] text-[#423E51] break-all">
                            {row.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="bg-white border border-[#EBE9EF] rounded-[13px] p-[13px] w-[240px] h-[245px] flex items-center justify-center shrink-0">
                {qrValue ? (
                  <div className="relative">
                    <QRCodeSVG
                      value={qrValue}
                      size={214}
                      level="H"
                      imageSettings={
                        config.showQrLogo
                          ? {
                              src: eightyEightPayIcon,
                              width: 68,
                              height: 68,
                              excavate: true,
                            }
                          : undefined
                      }
                    />
                  </div>
                ) : (
                  <p className="text-[12px] text-[#EB3366] text-center max-w-[150px]">
                    QR details are not available yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF] mt-auto" />
    </div>
  );
}
